import _ from 'lodash'
import {createContext, FC, useContext, useEffect, useReducer} from 'react'

import {useRouter} from '@/utils/hooks/useRouter'
import {useTrademarkWatchSubscriptionCount} from '@/utils/user-portal/dataFetchers/trademarkWatchSubscription'

enum MonitoringUrlParms {
  ACTIVE_ID_URL_PARAM = 'activeTrademarkMonitoringResultId',
  MONITORING_DRAWER_OPEN_URL_PARAM = 'monitoringDrawerOpen',
}

type State = {
  isOpened: boolean
  activeId: string | null | undefined
}

type MonitoringDrawerContextType = {
  close: () => void
  open: (id?: string | null) => void
  subscriptionCount: number | undefined
} & State

const MonitoringDrawerContext = createContext<MonitoringDrawerContextType>({
  close: _.identity,
  open: _.identity,
  isOpened: false,
  activeId: undefined,
  subscriptionCount: 0,
})

export const useMonitoringDrawerContext = (): MonitoringDrawerContextType =>
  useContext(MonitoringDrawerContext)

enum Actions {
  HIDE = 'hide',
  SHOW = 'show',
}

const initialState = {
  isOpened: false,
  activeId: null,
} as State

const reducer = (state: State, action: {type: Actions; payload?: Partial<State>}): State => {
  switch (action.type) {
    case Actions.SHOW:
      return {
        ...state,
        ...action.payload,
      }
    case Actions.HIDE:
      return {...state, isOpened: false}
    default:
      return state
  }
}

export const MonitoringDrawerProvider: FC<React.PropsWithChildren<unknown>> = ({children}) => {
  const router = useRouter()
  const [state, dispatch] = useReducer(reducer, initialState)
  const {
    [MonitoringUrlParms.ACTIVE_ID_URL_PARAM]: activeIdUrlParam,
    [MonitoringUrlParms.MONITORING_DRAWER_OPEN_URL_PARAM]: isOpenedUrlParam,
  } = router.query
  const {data: subscriptionCount} = useTrademarkWatchSubscriptionCount()

  useEffect(() => {
    if (isOpenedUrlParam === 'true') {
      dispatch({
        type: Actions.SHOW,
        payload: {
          activeId: (activeIdUrlParam as string) ?? null,
          isOpened: true,
        },
      })
    } else {
      dispatch({
        type: Actions.HIDE,
      })
    }
  }, [isOpenedUrlParam, activeIdUrlParam])

  const close = () => {
    const url = new URL(window.location.href)
    url.searchParams.delete(MonitoringUrlParms.ACTIVE_ID_URL_PARAM)
    url.searchParams.delete(MonitoringUrlParms.MONITORING_DRAWER_OPEN_URL_PARAM)
    router.push(url)
  }

  const open = (activeTrademarkMonitoringResultId?: string | null) => {
    const url = new URL(window.location.href)
    if (activeTrademarkMonitoringResultId) {
      url.searchParams.set(
        MonitoringUrlParms.ACTIVE_ID_URL_PARAM,
        activeTrademarkMonitoringResultId,
      )
    } else {
      url.searchParams.delete(MonitoringUrlParms.ACTIVE_ID_URL_PARAM)
    }
    url.searchParams.set(MonitoringUrlParms.MONITORING_DRAWER_OPEN_URL_PARAM, 'true')
    router.push(url)
  }

  return (
    <MonitoringDrawerContext.Provider
      value={{
        close,
        open,
        subscriptionCount,
        ...state,
      }}
    >
      {children}
    </MonitoringDrawerContext.Provider>
  )
}
